import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { hide, show } from "@ebay/nice-modal-react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  DataTable,
  DataTableColumn,
  DataTablePagination,
  EMPTY_VALUE,
  StatusBadge,
  TableButton,
  TextCell,
} from "Components/UI/DataTable";
import ConfirmModal from "Components/UI/ConfirmModal/ConfirmModal";
import FaultRuleForm from "Components/FaultRules/FaultRuleForm/FaultRuleForm";
import { deleteFaultRule, getFaultRulesList } from "Redux/Faults/reducer";
import {
  selectFaultsRulesList,
  selectRulesCount,
  selectRulesError,
  selectRulesLoading,
} from "Redux/Faults/slice";
import { useAppDispatch } from "Redux/store";
import { LIMIT_PAGE } from "Shared/Constants/app";
import { useDebounce } from "Shared/Hook/use-debounce";
import { usePageInRange } from "Shared/Hook/usePageInRange";
import { IFaultsRule } from "Shared/Types/faults";
import SearchInput from "../../../Settings/Roles/SearchInput/SearchInput";
import { FAULT_PRIORITY } from "Components/Faults/FaultsTable/faultColumns";

const FaultRulesList = () => {
  const dispatch = useAppDispatch();
  const rules = useSelector(selectFaultsRulesList);
  const count = useSelector(selectRulesCount);
  const loading = useSelector(selectRulesLoading);
  const error = useSelector(selectRulesError);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  // The rule stays set while the form animates closed.
  const [editing, setEditing] = useState<IFaultsRule>();
  const [editOpen, setEditOpen] = useState(false);
  const debouncedSearch = useDebounce(search);

  const params = useMemo(
    () => ({
      search: debouncedSearch.trim(),
      limit: LIMIT_PAGE,
      offset: (page - 1) * LIMIT_PAGE,
    }),
    [debouncedSearch, page],
  );
  const load = () => dispatch(getFaultRulesList(params));

  useEffect(() => {
    load();
  }, [params]);

  // Deleting the last rule on the last page reloads an empty page; step back.
  usePageInRange(page, setPage, count, !loading && !error);

  const confirmDelete = (rule: IFaultsRule) =>
    show(ConfirmModal, {
      onClick: async () => {
        await dispatch(deleteFaultRule(rule.id));
        hide(ConfirmModal);
      },
    });

  const columns = useMemo<DataTableColumn<IFaultsRule>[]>(
    () => [
      {
        id: "code",
        header: "Code",
        meta: { width: 260 },
        cell: ({ row }) => {
          const priority = FAULT_PRIORITY[row.original.priority_status || ""];
          return (
            <TextCell
              strong
              primary={
                <>
                  {row.original.code}
                  {priority && (
                    <>
                      {" "}
                      <StatusBadge tone={priority.tone}>
                        {priority.label}
                      </StatusBadge>
                    </>
                  )}
                </>
              }
              secondary={row.original.name || undefined}
            />
          );
        },
      },
      {
        id: "description",
        header: "Description",
        meta: { truncate: true },
        cell: ({ row }) => row.original.description || EMPTY_VALUE,
      },
      {
        id: "actions",
        header: "Actions",
        meta: { width: 90, align: "right", interactive: true },
        cell: ({ row }) => (
          <TableButton
            aria-label={`Delete ${row.original.code}`}
            icon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => confirmDelete(row.original)}
          />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <DataTable<IFaultsRule>
        aria-label="Fault rules"
        variant="embedded"
        data={rules}
        columns={columns}
        loading={loading}
        error={error ? "Fault rules could not be loaded." : undefined}
        onRetry={load}
        onRowClick={(rule) => {
          setEditing(rule);
          setEditOpen(true);
        }}
        toolbar={
          <SearchInput
            value={search}
            inputProps={{ "aria-label": "Search fault rules" }}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        }
        empty={{
          title: params.search
            ? "No fault rules match your search."
            : "No fault rules yet.",
        }}
        footer={
          count > 0 ? (
            <DataTablePagination
              count={count}
              page={page}
              onPageChange={setPage}
              showPageSize={false}
              itemLabel="rules"
              disabled={loading}
            />
          ) : undefined
        }
      />
      <FaultRuleForm
        rule={editing}
        type="EDIT"
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </>
  );
};

export default FaultRulesList;
